/**
 * 发券申请页（运营主管提交 → 超管审批后系统才真正发券）。
 *
 * 设计：project-docs/specs/2026-10-15-ops-lead-role-design.md（D4 / I8 / §5-C）。
 *
 * 权限（后端 requirePermission 才是真拦截，这里只是体验层）：
 *  - voucher:request（ops_lead / 超管）：「新建申请」表单 + 「我的申请」表格（可撤回 pending）
 *  - voucher:approve（仅超管）：待审批列表（默认筛 pending）+ 详情抽屉（逐用户审批上下文）+ 通过 / 驳回 / 处理「结果不明」
 *
 * 通过的语义（务必让审批人看明白）：
 *  - 「通过」会**立即**向 N 位用户发放免费成书券，二次确认文案写明人数；
 *  - 发起人 ≠ 审批人：自己提交的申请不能自批（按钮禁用并说明）；
 *  - 发券结果不明（unknown）时系统不会自动重试，必须到「免单券」列表核对后点「已发出 / 未发出」。
 *
 * 实现注意（项目铁律）：
 *  - antd message.* 返回 thenable：react-query 的 onSuccess / onError 里必须块体且不 return，否则按钮 loading 多挂约 3 秒；
 *  - 同页多个 Form 各自唯一 name；Modal / Drawer 内容打开后才挂载，setFieldsValue 会落空 → 用 destroyOnClose + preserve=false + 提交走 form.submit()。
 */
import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Drawer,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usePermission } from '../../hooks/usePermission';
import { useSession } from '../../stores/session';
import { PERMISSIONS } from '../../contracts/permissions';
import {
  approveVoucherGrantRequest,
  cancelVoucherGrantRequest,
  createVoucherGrantRequest,
  getVoucherGrantRequest,
  listVoucherGrantRequests,
  rejectVoucherGrantRequest,
  resolveVoucherGrantRequest,
  type ApprovalContextRow,
  type ResolveOutcome,
  type VoucherGrantRequestDisplayStatus,
  type VoucherGrantRequestDto,
} from '../../api/voucher-grant-requests';

const { Text, Paragraph } = Typography;

/** react-query 的 key 前缀：任何写操作后整体失效（列表 / 详情 / 首页待审批卡片共用） */
export const VOUCHER_REQUESTS_QK = 'voucher-grant-requests';

export const MAX_USERS_PER_REQUEST = 100;
const NOTE_MIN = 5;
const NOTE_MAX = 200;

// ── 小工具 ───────────────────────────────────────────────────────────────────

/** 多行文本 → 去重后的用户 ID 列表（换行 / 空格 / 逗号 / 分号 / 顿号均可分隔）。 */
export function parseUserIds(text: string | undefined | null): { ids: string[]; duplicates: number } {
  const raw = (text ?? '')
    .split(/[\s,;，；、]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const ids = Array.from(new Set(raw));
  return { ids, duplicates: raw.length - ids.length };
}

function errMsg(e: unknown, fallback: string): string {
  const err = e as { response?: { data?: { message?: string } }; message?: string } | undefined;
  return err?.response?.data?.message ?? err?.message ?? fallback;
}

function fmt(ms: number | null | undefined): string {
  return ms == null ? '-' : new Date(ms).toLocaleString('zh-CN');
}

/** 5..200 字（去首尾空白后）的实时校验器，申请原因 / 驳回理由 / 核对说明共用。 */
function noteValidator(what: string) {
  return async (_: unknown, v: string | undefined) => {
    const len = (v ?? '').trim().length;
    if (len === 0) return; // 空值交给 required 规则，避免重复报错
    if (len < NOTE_MIN) throw new Error(`${what}至少 ${NOTE_MIN} 个字`);
    if (len > NOTE_MAX) throw new Error(`${what}最多 ${NOTE_MAX} 个字`);
  };
}

const STATUS_META: Record<VoucherGrantRequestDisplayStatus, { label: string; color: string; hint: string }> = {
  pending: { label: '待审批', color: 'processing', hint: '等待超管审批；超过 7 天未处理会自动过期' },
  approving: { label: '发券中', color: 'blue', hint: '审批人已点通过，系统正在发券，请稍候刷新' },
  approved: { label: '已通过', color: 'success', hint: '已通过并发券' },
  rejected: { label: '已驳回', color: 'error', hint: '审批人已驳回，原因见右侧' },
  cancelled: { label: '已撤回', color: 'default', hint: '申请人已撤回' },
  failed: { label: '发券失败', color: 'warning', hint: '发券没有成功（这批用户没有收到券），等待审批人重新处理' },
  unknown: {
    label: '结果不明',
    color: 'error',
    hint: '系统没有拿到发券结果，这批用户可能已收到券也可能没有，已交超管人工核对',
  },
  expired: { label: '已过期', color: 'default', hint: '超过 7 天未处理，已不能通过；可撤回后重新提交' },
};

function StatusTag({ status }: { status: VoucherGrantRequestDisplayStatus }) {
  const m = STATUS_META[status];
  return (
    <Tooltip title={m.hint}>
      <Tag color={m.color} data-testid={`status-${status}`}>
        {m.label}
      </Tag>
    </Tooltip>
  );
}

// ── 新建申请 ─────────────────────────────────────────────────────────────────

interface NewRequestValues {
  userIdsText: string;
  expireAt: Dayjs;
  reason: string;
}

function NewRequestForm() {
  const qc = useQueryClient();
  const [form] = Form.useForm<NewRequestValues>();
  const text = Form.useWatch('userIdsText', form);
  const { ids, duplicates } = parseUserIds(text);
  const overLimit = ids.length > MAX_USERS_PER_REQUEST;

  const mutation = useMutation({
    mutationFn: createVoucherGrantRequest,
    onSuccess: () => {
      message.success('申请已提交，等待超管审批');
      form.resetFields();
      qc.invalidateQueries({ queryKey: [VOUCHER_REQUESTS_QK] });
    },
    onError: (e) => {
      message.error(errMsg(e, '提交失败'));
    },
  });

  function onFinish(v: NewRequestValues) {
    const parsed = parseUserIds(v.userIdsText);
    mutation.mutate({
      userIds: parsed.ids,
      expireAt: v.expireAt.valueOf(),
      reason: v.reason.trim(),
    });
  }

  return (
    <Card size="small" title="新建申请" style={{ marginBottom: 16 }} data-testid="request-form-card">
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="提交后由超管审批；通过后系统才会向这些用户发放免费成书券。申请 7 天内未处理会自动过期。"
      />
      <Form form={form} name="voucher-request-form" layout="vertical" onFinish={onFinish}>
        <Form.Item
          label={`用户 ID（一行一个，最多 ${MAX_USERS_PER_REQUEST} 个）`}
          name="userIdsText"
          rules={[
            { required: true, message: '请填写用户 ID' },
            {
              validator: async (_, v: string | undefined) => {
                const n = parseUserIds(v).ids.length;
                if (n > MAX_USERS_PER_REQUEST) throw new Error(`一次最多 ${MAX_USERS_PER_REQUEST} 位用户（去重后 ${n} 位）`);
              },
            },
          ]}
          extra={
            <Text type={overLimit ? 'danger' : 'secondary'} data-testid="request-user-count">
              去重后 {ids.length} 人
              {duplicates > 0 ? `（已合并 ${duplicates} 条重复）` : ''}
              {overLimit ? `，超出 ${MAX_USERS_PER_REQUEST} 人上限，请拆成多次申请` : ''}
            </Text>
          }
        >
          <Input.TextArea rows={6} placeholder={'u-1\nu-2\nu-3'} />
        </Form.Item>
        <Form.Item
          label="券有效期（到期后券作废）"
          name="expireAt"
          rules={[
            { required: true, message: '请选择券有效期' },
            {
              validator: async (_, v: Dayjs | undefined) => {
                if (v && v.valueOf() <= Date.now()) throw new Error('券有效期必须晚于现在');
              },
            },
          ]}
        >
          <DatePicker showTime style={{ width: 260 }} disabledDate={(d) => d.isBefore(dayjs(), 'day')} />
        </Form.Item>
        <Form.Item
          label={`申请原因（${NOTE_MIN}–${NOTE_MAX} 字，审批人据此决定是否通过）`}
          name="reason"
          rules={[{ required: true, message: '请填写申请原因' }, { validator: noteValidator('申请原因') }]}
        >
          <Input.TextArea rows={3} maxLength={NOTE_MAX} showCount />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={mutation.isPending} disabled={overLimit} data-testid="request-submit-btn">
          提交申请
        </Button>
      </Form>
    </Card>
  );
}

// ── 我的申请 ─────────────────────────────────────────────────────────────────

function MyRequestsTable() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const query = useQuery({
    queryKey: [VOUCHER_REQUESTS_QK, 'mine', page],
    queryFn: () => listVoucherGrantRequests({ mine: true, page, pageSize }),
  });

  const cancelMutation = useMutation({
    mutationFn: cancelVoucherGrantRequest,
    onSuccess: () => {
      message.success('已撤回');
      qc.invalidateQueries({ queryKey: [VOUCHER_REQUESTS_QK] });
    },
    onError: (e) => {
      message.error(errMsg(e, '撤回失败'));
      qc.invalidateQueries({ queryKey: [VOUCHER_REQUESTS_QK] });
    },
  });

  const columns: ColumnsType<VoucherGrantRequestDto> = [
    { title: '申请时间', dataIndex: 'createdAt', key: 'createdAt', width: 170, render: fmt },
    { title: '人数', dataIndex: 'userCount', key: 'userCount', width: 70 },
    { title: '券有效期', dataIndex: 'expireAt', key: 'expireAt', width: 170, render: fmt },
    { title: '申请原因', dataIndex: 'reason', key: 'reason', ellipsis: true },
    {
      title: '状态',
      dataIndex: 'displayStatus',
      key: 'displayStatus',
      width: 110,
      render: (s: VoucherGrantRequestDisplayStatus) => <StatusTag status={s} />,
    },
    {
      title: '审批结果',
      key: 'result',
      width: 240,
      render: (_: unknown, r) => {
        if (r.decisionReason) return <Text>{r.decisionReason}</Text>;
        if (r.displayStatus === 'failed' || r.displayStatus === 'unknown') {
          return <Text type="secondary">{STATUS_META[r.displayStatus].hint}</Text>;
        }
        if (r.displayStatus === 'expired') return <Text type="secondary">{STATUS_META.expired.hint}</Text>;
        return '-';
      },
    },
    {
      title: '操作',
      key: 'actions',
      width: 90,
      render: (_: unknown, r) =>
        r.status === 'pending' ? (
          <Popconfirm
            title="撤回这条申请？"
            description="撤回后不能恢复，需要的话请重新提交。"
            okText="撤回"
            cancelText="再想想"
            onConfirm={() => cancelMutation.mutate(r.id)}
          >
            <Button size="small" danger loading={cancelMutation.isPending && cancelMutation.variables === r.id} data-testid={`request-cancel-${r.id}`}>
              撤回
            </Button>
          </Popconfirm>
        ) : null,
    },
  ];

  return (
    <Card size="small" title="我的申请" data-testid="my-requests-card">
      <Table<VoucherGrantRequestDto>
        rowKey="id"
        size="small"
        loading={query.isLoading}
        dataSource={query.data?.items ?? []}
        columns={columns}
        pagination={{
          current: page,
          pageSize,
          total: query.data?.total ?? 0,
          showSizeChanger: false,
          onChange: setPage,
        }}
        data-testid="my-requests-table"
      />
    </Card>
  );
}

// ── 审批：列表 + 详情抽屉 ─────────────────────────────────────────────────────

const STATUS_FILTER_OPTIONS: { value: VoucherGrantRequestDisplayStatus | ''; label: string }[] = [
  { value: 'pending', label: '待审批' },
  { value: 'unknown', label: '结果不明（需人工核对）' },
  { value: 'failed', label: '发券失败' },
  { value: 'approving', label: '发券中' },
  { value: 'expired', label: '已过期' },
  { value: 'approved', label: '已通过' },
  { value: 'rejected', label: '已驳回' },
  { value: 'cancelled', label: '已撤回' },
  { value: '', label: '全部' },
];

function ApprovalPanel() {
  const [status, setStatus] = useState<VoucherGrantRequestDisplayStatus | ''>('pending');
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<string | null>(null);
  const pageSize = 10;
  const query = useQuery({
    queryKey: [VOUCHER_REQUESTS_QK, 'approve', status, page],
    queryFn: () => listVoucherGrantRequests({ status: status || undefined, page, pageSize }),
  });

  const columns: ColumnsType<VoucherGrantRequestDto> = [
    { title: '申请人', dataIndex: 'applicantNickname', key: 'applicant', width: 130, ellipsis: true },
    { title: '申请时间', dataIndex: 'createdAt', key: 'createdAt', width: 170, render: fmt },
    { title: '人数', dataIndex: 'userCount', key: 'userCount', width: 70 },
    { title: '券有效期', dataIndex: 'expireAt', key: 'expireAt', width: 170, render: fmt },
    { title: '申请原因', dataIndex: 'reason', key: 'reason', ellipsis: true },
    {
      title: '状态',
      dataIndex: 'displayStatus',
      key: 'displayStatus',
      width: 110,
      render: (s: VoucherGrantRequestDisplayStatus) => <StatusTag status={s} />,
    },
    {
      title: '操作',
      key: 'actions',
      width: 100,
      render: (_: unknown, r) => (
        <Button size="small" type={r.displayStatus === 'pending' || r.displayStatus === 'unknown' ? 'primary' : 'default'} onClick={() => setOpenId(r.id)} data-testid={`request-open-${r.id}`}>
          {r.displayStatus === 'pending' ? '审批' : '查看'}
        </Button>
      ),
    },
  ];

  return (
    <div data-testid="approval-panel">
      <Space style={{ marginBottom: 16 }}>
        <Text>状态</Text>
        <Select
          value={status}
          style={{ width: 220 }}
          options={STATUS_FILTER_OPTIONS}
          onChange={(v) => {
            setStatus(v);
            setPage(1);
          }}
          data-testid="approval-status-filter"
        />
      </Space>
      <Table<VoucherGrantRequestDto>
        rowKey="id"
        size="small"
        loading={query.isLoading}
        dataSource={query.data?.items ?? []}
        columns={columns}
        pagination={{
          current: page,
          pageSize,
          total: query.data?.total ?? 0,
          showSizeChanger: false,
          onChange: setPage,
        }}
        data-testid="approval-table"
      />
      <Drawer title="发券申请详情" open={openId !== null} onClose={() => setOpenId(null)} width={760} destroyOnClose>
        {openId && <RequestDetail key={openId} id={openId} />}
      </Drawer>
    </div>
  );
}

function ContextTable({ rows }: { rows: ApprovalContextRow[] }) {
  const withVoucher = rows.filter((r) => r.validVoucherCount > 0).length;
  const withRecent = rows.filter((r) => r.recentVoucherCount > 0).length;
  const columns: ColumnsType<ApprovalContextRow> = [
    { title: '用户 ID', dataIndex: 'userId', key: 'userId', ellipsis: true, width: 170 },
    {
      title: '昵称',
      key: 'nickname',
      width: 130,
      render: (_: unknown, r) => (r.exists ? (r.nickname ?? '-') : <Tag color="error">用户不存在</Tag>),
    },
    { title: '注册时间', dataIndex: 'registeredAt', key: 'registeredAt', width: 170, render: fmt },
    { title: '已付订单数', dataIndex: 'paidOrderCount', key: 'paid', width: 100 },
    {
      title: '有效券数',
      dataIndex: 'validVoucherCount',
      key: 'valid',
      width: 90,
      render: (n: number) => (n > 0 ? <Tag color="orange">{n}</Tag> : n),
    },
    {
      title: '近 30 天已领券数',
      dataIndex: 'recentVoucherCount',
      key: 'recent',
      width: 130,
      render: (n: number) => (n > 0 ? <Tag color="orange">{n}</Tag> : n),
    },
  ];
  return (
    <>
      <Paragraph type="secondary" style={{ marginBottom: 8 }}>
        共 {rows.length} 位用户：{withVoucher} 位当前已有可用券，{withRecent} 位近 30 天领过券（橙色标出，请留意是否重复发放）。
      </Paragraph>
      <Table<ApprovalContextRow>
        rowKey="userId"
        size="small"
        columns={columns}
        dataSource={rows}
        pagination={rows.length > 20 ? { pageSize: 20, showSizeChanger: false } : false}
        data-testid="approval-context-table"
      />
    </>
  );
}

function RequestDetail({ id }: { id: string }) {
  const qc = useQueryClient();
  const canApprove = usePermission(PERMISSIONS.VOUCHER_APPROVE);
  const myId = useSession((s) => s.actorId);
  const query = useQuery({
    queryKey: [VOUCHER_REQUESTS_QK, 'detail', id],
    queryFn: () => getVoucherGrantRequest(id),
  });

  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [resolveOutcome, setResolveOutcome] = useState<ResolveOutcome | null>(null);
  const [rejectForm] = Form.useForm<{ reason: string }>();
  const [resolveForm] = Form.useForm<{ note: string }>();

  const refresh = () => {
    qc.invalidateQueries({ queryKey: [VOUCHER_REQUESTS_QK] });
  };

  const approveMutation = useMutation({
    mutationFn: () => approveVoucherGrantRequest(id),
    onSuccess: (r) => {
      setApproveOpen(false);
      if (r.status === 'unknown') {
        message.warning(r.message ?? '结果不明，请到免单券列表核对这批用户是否已收到券，再在审批页处理', 8);
      } else {
        message.success(`已通过，已向 ${query.data?.userCount ?? ''} 位用户发放免费成书券`);
      }
      refresh();
    },
    onError: (e) => {
      // 409：cp 明确拒绝（没发出，申请已退回「发券失败」）/ 已被别人处理；状态都可能变了，刷新
      message.error(errMsg(e, '通过失败'), 6);
      setApproveOpen(false);
      refresh();
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (reason: string) => rejectVoucherGrantRequest(id, reason),
    onSuccess: () => {
      message.success('已驳回');
      setRejectOpen(false);
      rejectForm.resetFields();
      refresh();
    },
    onError: (e) => {
      message.error(errMsg(e, '驳回失败'));
      refresh();
    },
  });

  const resolveMutation = useMutation({
    mutationFn: (vars: { outcome: ResolveOutcome; note: string }) => resolveVoucherGrantRequest(id, vars.outcome, vars.note),
    onSuccess: (r) => {
      message.success(r.status === 'approved' ? '已标记为「已发出」' : '已标记为「未发出」，可重新通过');
      setResolveOutcome(null);
      resolveForm.resetFields();
      refresh();
    },
    onError: (e) => {
      message.error(errMsg(e, '处理失败'));
      refresh();
    },
  });

  if (query.isLoading) return <Spin />;
  if (query.isError || !query.data) {
    return <Alert type="error" showIcon message="详情加载失败" description={errMsg(query.error, '请稍后重试')} />;
  }

  const d = query.data;
  const shown = d.displayStatus;
  const isOwn = !!myId && d.applicantId === myId;
  const canDecide = canApprove && (d.status === 'pending' || d.status === 'failed');
  const approveBlockedReason = isOwn
    ? '不能审批自己提交的申请'
    : shown === 'expired'
      ? '申请已超过 7 天，不能再通过，请驳回并让申请人重提'
      : d.expireAt <= Date.now()
        ? '券有效期已过，请驳回并让申请人重提'
        : null;

  return (
    <div data-testid="request-detail">
      {shown === 'unknown' && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          data-testid="unknown-alert"
          message="结果不明：这批用户可能已收到券，也可能没有"
          description={
            <>
              <div>系统调用发券服务时没有拿到明确结果。为避免重复发券，系统不会自动重试。</div>
              <div>请先到「免单券」列表，按下面的用户 ID 核对这批用户是否已收到券，再点下方按钮处理。</div>
              {d.errorMessage && <div>原因：{d.errorMessage}</div>}
              {d.voucherIds && d.voucherIds.length > 0 && <div>已记录的券 ID：{d.voucherIds.join('、')}</div>}
            </>
          }
        />
      )}
      {shown === 'failed' && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="上次发券没有成功（这批用户没有收到券）"
          description={`${d.errorMessage ?? ''} 可重新通过，或驳回。`}
        />
      )}
      {shown === 'expired' && (
        <Alert type="warning" showIcon style={{ marginBottom: 16 }} message="申请已超过 7 天未处理，不能再通过，请驳回并让申请人重提。" />
      )}
      {shown === 'approving' && (
        <Alert type="info" showIcon style={{ marginBottom: 16 }} message="审批人已点通过，系统正在发券，请稍后刷新。" />
      )}

      <Descriptions column={2} size="small" bordered style={{ marginBottom: 16 }}>
        <Descriptions.Item label="申请人">
          {d.applicantNickname}
          <Text type="secondary">（{d.applicantId}）</Text>
        </Descriptions.Item>
        <Descriptions.Item label="状态">
          <StatusTag status={shown} />
        </Descriptions.Item>
        <Descriptions.Item label="申请时间">{fmt(d.createdAt)}</Descriptions.Item>
        <Descriptions.Item label="券有效期">{fmt(d.expireAt)}</Descriptions.Item>
        <Descriptions.Item label="人数">{d.userCount}</Descriptions.Item>
        <Descriptions.Item label="券类型">免费成书券</Descriptions.Item>
        <Descriptions.Item label="申请原因" span={2}>
          {d.reason}
        </Descriptions.Item>
        {d.decidedBy && (
          <Descriptions.Item label="审批人 / 时间" span={2}>
            {d.decidedByNickname ?? d.decidedBy}，{fmt(d.decidedAt)}
          </Descriptions.Item>
        )}
        {d.decisionReason && (
          <Descriptions.Item label="审批 / 核对说明" span={2}>
            {d.decisionReason}
          </Descriptions.Item>
        )}
        {d.voucherIds && d.voucherIds.length > 0 && shown !== 'unknown' && (
          <Descriptions.Item label="发出的券 ID" span={2}>
            {d.voucherIds.join('、')}
          </Descriptions.Item>
        )}
      </Descriptions>

      <Typography.Title level={5}>逐用户审批上下文</Typography.Title>
      <ContextTable rows={d.context} />

      {canApprove && (
        <Space style={{ marginTop: 16 }} wrap data-testid="request-actions">
          {canDecide && (
            <>
              <Tooltip title={approveBlockedReason ?? ''}>
                <span>
                  <Button type="primary" disabled={approveBlockedReason !== null} onClick={() => setApproveOpen(true)} data-testid="request-approve-btn">
                    {d.status === 'failed' ? '重新通过' : '通过'}
                  </Button>
                </span>
              </Tooltip>
              <Button danger onClick={() => setRejectOpen(true)} data-testid="request-reject-btn">
                驳回
              </Button>
            </>
          )}
          {shown === 'unknown' && !isOwn && (
            <>
              <Button onClick={() => setResolveOutcome('granted')} data-testid="request-resolve-granted-btn">
                已核对：已发出
              </Button>
              <Button onClick={() => setResolveOutcome('not_granted')} data-testid="request-resolve-not-granted-btn">
                已核对：未发出
              </Button>
            </>
          )}
          {isOwn && <Text type="secondary">这是你自己提交的申请，需要由其他审批人处理。</Text>}
        </Space>
      )}

      {/* 通过：二次确认（文案写明人数） */}
      <Modal
        title="确认通过并发券"
        open={approveOpen}
        onCancel={() => setApproveOpen(false)}
        onOk={() => approveMutation.mutate()}
        okText="确认通过"
        cancelText="取消"
        confirmLoading={approveMutation.isPending}
        destroyOnClose
        data-testid="approve-confirm-modal"
      >
        <Alert
          type="warning"
          showIcon
          message={`将立即向 ${d.userCount} 位用户发放免费成书券`}
          description={`券有效期至 ${fmt(d.expireAt)}。发出后用户会收到通知；如需收回，到「免单券」页逐张撤销。`}
        />
      </Modal>

      {/* 驳回：理由必填 */}
      <Modal
        title="驳回申请"
        open={rejectOpen}
        onCancel={() => setRejectOpen(false)}
        onOk={() => rejectForm.submit()}
        okText="确认驳回"
        cancelText="取消"
        okButtonProps={{ danger: true }}
        confirmLoading={rejectMutation.isPending}
        destroyOnClose
        data-testid="reject-modal"
      >
        <Form form={rejectForm} name="voucher-request-reject-form" layout="vertical" preserve={false} onFinish={(v) => rejectMutation.mutate(v.reason.trim())}>
          <Form.Item
            label={`驳回理由（${NOTE_MIN}–${NOTE_MAX} 字，申请人可见）`}
            name="reason"
            rules={[{ required: true, message: '请填写驳回理由' }, { validator: noteValidator('驳回理由') }]}
          >
            <Input.TextArea rows={3} maxLength={NOTE_MAX} showCount />
          </Form.Item>
        </Form>
      </Modal>

      {/* 处理「结果不明」：核对说明必填 */}
      <Modal
        title={resolveOutcome === 'granted' ? '已核对：这批用户已收到券' : '已核对：这批用户没有收到券'}
        open={resolveOutcome !== null}
        onCancel={() => setResolveOutcome(null)}
        onOk={() => resolveForm.submit()}
        okText="提交核对结果"
        cancelText="取消"
        confirmLoading={resolveMutation.isPending}
        destroyOnClose
        data-testid="resolve-modal"
      >
        <Alert
          type={resolveOutcome === 'granted' ? 'info' : 'warning'}
          showIcon
          style={{ marginBottom: 16 }}
          message={
            resolveOutcome === 'granted'
              ? '申请将标记为「已通过」，不会再发券。'
              : '申请将回到「发券失败」，之后可以重新通过（会再发一次券），请确认这批用户确实没有收到券。'
          }
        />
        <Form
          form={resolveForm}
          name="voucher-request-resolve-form"
          layout="vertical"
          preserve={false}
          onFinish={(v) => {
            if (resolveOutcome) resolveMutation.mutate({ outcome: resolveOutcome, note: v.note.trim() });
          }}
        >
          <Form.Item
            label={`核对说明（${NOTE_MIN}–${NOTE_MAX} 字，例如核对了哪些用户、依据是什么）`}
            name="note"
            rules={[{ required: true, message: '请填写核对说明' }, { validator: noteValidator('核对说明') }]}
          >
            <Input.TextArea rows={3} maxLength={NOTE_MAX} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

// ── 页面入口 ─────────────────────────────────────────────────────────────────

export default function VoucherRequestsPage() {
  const canRequest = usePermission(PERMISSIONS.VOUCHER_REQUEST);
  const canApprove = usePermission(PERMISSIONS.VOUCHER_APPROVE);

  if (!canRequest && !canApprove) {
    return (
      <Card>
        <Alert
          type="error"
          showIcon
          message="无访问权限"
          description="发券申请需要 voucher:request（提交申请）或 voucher:approve（审批）权限，请联系超管。"
        />
      </Card>
    );
  }

  const items = [
    ...(canApprove ? [{ key: 'approve', label: '审批', children: <ApprovalPanel /> }] : []),
    ...(canRequest
      ? [
          {
            key: 'mine',
            label: '我的申请',
            children: (
              <>
                <NewRequestForm />
                <MyRequestsTable />
              </>
            ),
          },
        ]
      : []),
  ];

  return (
    <Card title="发券申请" data-testid="voucher-requests-page">
      <Tabs defaultActiveKey={canApprove ? 'approve' : 'mine'} items={items} />
    </Card>
  );
}
